import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { apiUrl } from '../api';

export const analyzeTreeImage = async (base64Image: string) => {
  try {
    const response = await fetch(apiUrl('/api/ai/analyze'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        base64Data: base64Image,
        mimeType: 'image/jpeg',
        prompt: "Analyze this tree. Identify the species if possible, assess its overall health (EXCELLENT, GOOD, FAIR, POOR, CRITICAL), and provide a brief recommendation for service (Pruning, Removal, or Maintenance). Respond in JSON format."
      })
    });
    const data = await response.json();
    const jsonStr = data.text?.trim();
    return jsonStr ? JSON.parse(jsonStr) : null;
  } catch (error) {
    console.error("Gemini Image Analysis Error:", error);
    return null;
  }
};

export const analyzeAerialSurvey = async (base64Image: string) => {
  try {
    const response = await fetch(apiUrl('/api/ai/analyze'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        base64Data: base64Image,
        mimeType: 'image/jpeg',
        prompt: "This is an aerial drone photograph of a residential property for tree care assessment. Identify potential hazards (overhanging limbs near roofs/wires), count visible large trees, and assess general canopy density. Provide a concise summary for an arborist's report. Respond in JSON."
      })
    });
    const data = await response.json();
    return JSON.parse(data.text || '{}');
  } catch (error) {
    console.error("Aerial Analysis Error:", error);
    return null;
  }
};

export const extractReceiptData = async (base64Image: string) => {
  try {
    const response = await fetch(apiUrl('/api/ai/analyze'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        base64Data: base64Image,
        mimeType: 'image/jpeg',
        prompt: "Extract receipt information: merchant name, total amount (number), date, and a likely expense category (e.g., Fuel, Equipment, Supplies). Respond in JSON."
      })
    });
    const data = await response.json();
    return JSON.parse(data.text || '{}');
  } catch (error) {
    console.error("Receipt Extraction Error:", error);
    return null;
  }
};

export const extractBusinessCardData = async (base64Image: string) => {
  try {
    const response = await fetch(apiUrl('/api/ai/analyze'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        base64Data: base64Image,
        mimeType: 'image/jpeg',
        prompt: "Extract contact details from this business card. Return clientName, email, phone, and address if available. Respond in JSON."
      })
    });
    const data = await response.json();
    return JSON.parse(data.text || '{}');
  } catch (error) {
    console.error("Business Card Extraction Error:", error);
    return null;
  }
};

export const analyzeLocation = async (prompt: string) => {
  try {
    const response = await fetch(apiUrl('/api/ai/chat'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ message: prompt, model: 'gemini-3.6-flash' })
    });
    const data = await response.json();
    return data.text || '';
  } catch (error) {
    console.error("Location Analysis Error:", error);
    return '';
  }
};

export const analyzeDocument = async (base64Image: string) => {
  try {
    const response = await fetch(apiUrl('/api/ai/analyze'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        base64Data: base64Image,
        mimeType: 'image/jpeg',
        prompt: "This is a document (manual, warranty, or permit). Extract the title, determine the type (Manual, Warranty, Permit, or Other), and provide a concise 2-sentence summary. Respond in JSON."
      })
    });
    const data = await response.json();
    return JSON.parse(data.text || '{}');
  } catch (error) {
    console.error("Document Analysis Error:", error);
    return null;
  }
};

// ── Compendium Context: Firestore-primary, local-fallback ────────────

interface FirestoreKnowledgeEntry {
  question: string;
  answer: string;
  tags: string[];
  section: string;
  icon: string;
}

/**
 * Builds arboriculture system context for the AI consultant.
 * Tries Firestore first (for dynamically updated knowledge without redeploy),
 * falls back to bundled local compendium data.
 */
export const getCompendiumContext = async (): Promise<string> => {
  // 1. Try Firestore — allows updating knowledge without redeploying the app
  try {
    const snapshot = await getDocs(collection(db, 'treeKnowledge'));
    if (!snapshot.empty) {
      // Group entries by section to preserve the original structure
      const sections = new Map<string, { icon: string; entries: { q: string; a: string }[] }>();
      snapshot.forEach(d => {
        const data = d.data() as FirestoreKnowledgeEntry;
        const sec = data.section || 'General';
        if (!sections.has(sec)) {
          sections.set(sec, { icon: data.icon || '🌳', entries: [] });
        }
        sections.get(sec)!.entries.push({ q: data.question, a: data.answer });
      });

      const lines: string[] = [
        'You are the GreenTree Arboriculture AI Consultant serving Columbus, GA and Phenix City, AL.',
        'Use the following verified arboriculture knowledge to answer questions accurately.',
        'Always cite ANSI A300 standards for pruning and ANSI Z133-2026 for safety.',
        '',
      ];
      for (const [title, sec] of sections) {
        lines.push(`## ${sec.icon} ${title}`);
        for (const e of sec.entries) {
          lines.push(`Q: ${e.q}`);
          lines.push(`A: ${e.a}`);
          lines.push('');
        }
      }

      // Also try to fetch alerts
      try {
        const alertsSnap = await getDocs(collection(db, 'treeAlerts'));
        if (!alertsSnap.empty) {
          lines.push('## Emergency Alerts');
          alertsSnap.forEach(a => {
            const d = a.data();
            lines.push(`- [${(d.urgency || 'medium').toUpperCase()}] ${d.title}: ${d.status}. ${d.action}`);
          });
        }
      } catch { /* alerts optional */ }

      // Also try pricing
      try {
        const pricingSnap = await getDocs(collection(db, 'treePricing'));
        if (!pricingSnap.empty) {
          lines.push('');
          lines.push('## Pricing Quick Reference (Columbus GA)');
          pricingSnap.forEach(p => {
            const d = p.data();
            lines.push(`- ${d.service}: ${d.range}`);
          });
        }
      } catch { /* pricing optional */ }

      console.log(`[Compendium] Loaded ${snapshot.size} knowledge entries from Firestore`);
      return lines.join('\n');
    }
  } catch (e) {
    console.warn('[Compendium] Firestore fetch failed, falling back to local data:', (e as Error).message);
  }

  // 2. Fallback: bundled local compendium (always available in the app bundle)
  try {
    const { knowledgeAsSystemContext } = await import('../data/treecareknowledge');
    console.log('[Compendium] Loaded from local knowledge base');
    return knowledgeAsSystemContext();
  } catch (e) {
    console.error('[Compendium] Local knowledge import failed:', (e as Error).message);
    return '';
  }
};
